import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { generateSEOTags, locationKeywords } from '../utils/seo';

export default function SEOMetaTags() {
  const location = useLocation();
  const { language } = useLanguage();
  const path = location.pathname;
  const baseUrl = 'https://cliniquemainsdoucesplus.com';
  const currentUrl = `${baseUrl}${path}`;
  
  // Generate title and description based on current path
  const getPageMeta = () => {
    const defaultMeta = {
      title: language === 'fr' 
        ? 'Clinique Mains Douces Plus - Centre Médical à Simbock, Yaoundé' 
        : 'Clinique Mains Douces Plus - Medical Center in Simbock, Yaoundé',
      description: language === 'fr'
        ? 'Centre médical de confiance à Tradex Simbock, Yaoundé. Consultations générales, urgences 24h/24, pédiatrie, maternité. Médecins expérimentés et équipements modernes.'
        : 'Trusted medical center in Tradex Simbock, Yaoundé. General consultations, 24/7 emergencies, pediatrics, maternity. Experienced doctors and modern equipment.',
      keywords: locationKeywords.simbock.join(', ')
    };
    
    if (path.includes('/services')) {
      return {
        title: language === 'fr' 
          ? 'Nos Services Médicaux à Simbock - Clinique Mains Douces Plus' 
          : 'Our Medical Services in Simbock - Clinique Mains Douces Plus',
        description: language === 'fr'
          ? 'Découvrez nos services médicaux complets à Simbock : consultations, analyses, échographie, pédiatrie, maternité, urgences 24h/24. Prenez rendez-vous en ligne.'
          : 'Discover our complete medical services in Simbock: consultations, analysis, ultrasound, pediatrics, maternity, 24/7 emergencies. Book your appointment online.',
        keywords: locationKeywords.services.join(', ')
      };
    }
    
    if (path.includes('/about')) {
      return {
        title: language === 'fr' 
          ? 'À Propos de Notre Clinique à Simbock - Clinique Mains Douces Plus' 
          : 'About Our Clinic in Simbock - Clinique Mains Douces Plus',
        description: language === 'fr'
          ? 'Depuis 1997, notre clinique à Tradex Simbock offre des soins de qualité. Découvrez notre histoire, nos valeurs et notre équipe dévouée.'
          : 'Since 1997, our clinic in Tradex Simbock has been providing quality care. Discover our history, values, and dedicated team.',
        keywords: 'histoire clinique simbock, équipe médicale yaoundé, valeurs clinique'
      };
    }
    
    if (path.includes('/contact')) {
      return {
        title: language === 'fr' 
          ? 'Contactez la Clinique Mains Douces Plus à Simbock, Yaoundé' 
          : 'Contact Clinique Mains Douces Plus in Simbock, Yaoundé',
        description: language === 'fr'
          ? 'Contactez-nous à Tradex Simbock pour prendre rendez-vous ou pour toute urgence médicale. Téléphone, WhatsApp, email et formulaire en ligne.'
          : 'Contact us at Tradex Simbock to book an appointment or for any medical emergency. Phone, WhatsApp, email, and online form.',
        keywords: 'contact clinique simbock, téléphone urgence yaoundé, rendez-vous médical simbock'
      };
    }
    
    return defaultMeta;
  };
  
  const meta = getPageMeta();
  const seoTags = generateSEOTags(meta, language);
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTags.title}</title>
      <meta name="description" content={seoTags.description} />
      <meta name="keywords" content={seoTags.keywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Open Graph */}
      <meta property="og:title" content={seoTags.title} />
      <meta property="og:description" content={seoTags.description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={seoTags.ogImage} />
      <meta property="og:locale" content={seoTags.locale} />
      
      {/* Twitter */}
      <meta name="twitter:title" content={seoTags.title} />
      <meta name="twitter:description" content={seoTags.description} />
      <meta name="twitter:image" content={seoTags.ogImage} />
      
      {/* Location Meta Tags */}
      <meta name="geo.region" content="CM-CE" />
      <meta name="geo.placename" content="Simbock, Yaoundé, Cameroun" />
      <meta name="geo.position" content="3.866667;11.516667" />
      <meta name="ICBM" content="3.866667, 11.516667" />
      
      {/* Language Alternates */}
      <link rel="alternate" href={`${baseUrl}/fr${path}`} hreflang="fr" />
      <link rel="alternate" href={`${baseUrl}/en${path}`} hreflang="en" />
      <link rel="alternate" href={currentUrl} hreflang="x-default" />
    </Helmet>
  );
}